import React, { useState, useRef, useEffect } from "react";

type IDropdwonFilter<T> = {
  options?: T[];
  value?: T;
  onChange?: (props) => any;
  label?: string;
  id?: string;
  placeholder?: string;
  error?: unknown;
  required?: boolean;
  city?: boolean;
  openUp?: boolean;
};

const getErrorMessage = (error: unknown): string => {
  if (typeof error === "string") return error;
  if (
    error &&
    typeof error === "object" &&
    "message" in (error as { message?: unknown })
  ) {
    return getErrorMessage((error as { message?: unknown }).message);
  }
  return "";
};

const DropdownFilter = <T extends any>({
  options,
  value,
  onChange,
  label,
  id,
  placeholder,
  error,
  city = true,
  required = false,
  openUp = false,
}: IDropdwonFilter<T>) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const errorMessage = getErrorMessage(error);
  const ref = useRef(null);
  const dropdownRef = useRef(null);
  const touchStartY = useRef<number>(0);
  const touchStartTime = useRef<number>(0);
  const isScrolling = useRef<boolean>(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        ref.current &&
        !ref.current.contains(e.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    isScrolling.current = false;
  };

  const handleTouchMove = () => {
    isScrolling.current = true;
  };

  const handleTouchEnd = (option: T, e: React.TouchEvent) => {
    const touchEndY = e.changedTouches[0].clientY;
    const touchDuration = Date.now() - touchStartTime.current;
    const touchDistance = Math.abs(touchEndY - touchStartY.current);

    // Solo seleccionar si no hubo scroll (movimiento < 10px y duración < 300ms)
    if (!isScrolling.current && touchDistance < 10 && touchDuration < 300) {
      e.preventDefault();
      e.stopPropagation();
      selectOption(option);
    }

    isScrolling.current = false;
  };

  const filter = (options) => {
    return (options || []).filter((option) =>
      String(option?.[label] ?? "")
        .toLowerCase()
        .includes(query.toLowerCase()),
    );
  };
  const displayValue = () => {
    if (query.length > 0) return query;
    if (value) return value[label];
    return "";
  };

  const selectOption = (option: T) => {
    onChange(option);
    setOpen(false);
    setQuery("");
  };
  return (
    <div className="relative h-20 w-full">
      <div className="h-8">
        <p className="my-0 py-0 text-base">
          {placeholder}{" "}
          {required && <span className="text-3xl text-critical">*</span>}{" "}
          {errorMessage && (
            <span className="my-0 py-0 text-base text-critical">
              {errorMessage}
            </span>
          )}
        </p>
      </div>
      <div className="w-full">
        <div className="my-3 w-full">
          <input
            ref={ref}
            type="text"
            value={String(displayValue())}
            onChange={(e) => {
              setQuery(e.target.value);
              onChange(null);
            }}
            onFocus={() => setOpen(true)}
            className="w-full rounded-lg bg-gray px-3 py-2 text-textBlack"
            placeholder={placeholder}
          />
        </div>
      </div>
      <div
        ref={dropdownRef}
        className={`${open ? "absolute" : "hidden"} z-50 flex max-h-44 w-full flex-col overflow-auto rounded-xl bg-white px-3 py-2 shadow-md ${openUp ? "bottom-full -mb-10" : "mt-2"}`}
      >
        {filter(options).length > 0 ? (
          filter(options).map((option) => (
            <div
              key={option[id]}
              className={`option ${value === option ? "bg-buttonBlack text-white" : "hover:bg-gray-100"} my-1 min-h-[44px] cursor-pointer rounded-md px-3 py-2 transition-colors duration-150`}
              onClick={() => selectOption(option)}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={(e) => handleTouchEnd(option, e)}
            >
              {option[label]} {city && `- ${option?.departamento.nombre}`}
            </div>
          ))
        ) : (
          <div className="option min-h-[44px] px-3 py-2">No hay opciones</div>
        )}
      </div>
    </div>
  );
};

export default DropdownFilter;
